import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseDefaultedExpansionOptions {
  ids: string[];
  defaultExpanded: string[];
  resetKey: string;
}

interface ToggleOptions {
  force?: "open" | "closed";
}

export const useDefaultedExpansion = ({ ids, defaultExpanded, resetKey }: UseDefaultedExpansionOptions) => {
  const idsSignature = useMemo(() => ids.join("\u001f"), [ids]);
  const defaultExpandedSignature = useMemo(() => defaultExpanded.join("\u001f"), [defaultExpanded]);
  const resetKeyRef = useRef(resetKey);
  const manuallyClosedRef = useRef(new Set<string>());
  const [expanded, setExpanded] = useState<string[]>(() => unique(defaultExpanded));

  useEffect(() => {
    const idSet = new Set(ids);
    const nextDefaults = unique(defaultExpanded).filter((id) => idSet.has(id));

    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey;
      manuallyClosedRef.current = new Set();
      setExpanded(nextDefaults);
      return;
    }

    for (const id of manuallyClosedRef.current) {
      if (!idSet.has(id)) {
        manuallyClosedRef.current.delete(id);
      }
    }

    setExpanded((current) => {
      const next = current.filter((id) => idSet.has(id));
      for (const id of nextDefaults) {
        if (!manuallyClosedRef.current.has(id) && !next.includes(id)) {
          next.push(id);
        }
      }
      return sameItems(current, next) ? current : next;
    });
  }, [defaultExpanded, defaultExpandedSignature, ids, idsSignature, resetKey]);

  const toggle = useCallback((id: string, options?: ToggleOptions) => {
    setExpanded((current) => {
      const shouldOpen = options?.force === "open" || (options?.force !== "closed" && !current.includes(id));

      if (!shouldOpen) {
        manuallyClosedRef.current.add(id);
        const next = current.filter((item) => item !== id);
        return sameItems(current, next) ? current : next;
      }

      manuallyClosedRef.current.delete(id);
      if (current.includes(id)) {
        return current;
      }
      return [...current, id];
    });
  }, []);

  return { expanded, toggle };
};

const unique = (values: string[]) => Array.from(new Set(values));

const sameItems = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);
